import { PayCalendar, PayCalendarStatus, PayCycle } from '@aslanpay/portal-common-lib';
import axios from 'axios';
import { differenceInCalendarDays, parseISO } from 'date-fns';

export interface PaySchedule {
  lastCutoffDate?: string;
  lastPayday?: string;
  nextCutoffDate?: string;
  nextPayday?: string;
}

// Most recent date strictly before today (the last one that has already passed).
const lastBefore = (dates: string[], today: Date): string | undefined =>
  dates
    .filter((date) => differenceInCalendarDays(parseISO(date), today) < 0)
    .sort((first, second) => differenceInCalendarDays(parseISO(second), parseISO(first)))[0];

// Earliest date on or after today (the next upcoming one, including today).
const nextOnOrAfter = (dates: string[], today: Date): string | undefined =>
  dates
    .filter((date) => differenceInCalendarDays(parseISO(date), today) >= 0)
    .sort((first, second) => differenceInCalendarDays(parseISO(first), parseISO(second)))[0];

/**
 * Derives the last (most recently passed) and next (upcoming) cut-off dates and paydays from a
 * calendar's pay cycles, relative to today. "Last" is strictly in the past; "next" includes today.
 */
export const derivePaySchedule = (
  payCycles: PayCycle[] | undefined,
  today: Date = new Date()
): PaySchedule => {
  const cutoffs = (payCycles ?? []).map((cycle) => cycle.cutoffDate).filter(Boolean);
  const paydays = (payCycles ?? []).map((cycle) => cycle.payday).filter(Boolean);

  return {
    lastCutoffDate: lastBefore(cutoffs, today),
    lastPayday: lastBefore(paydays, today),
    nextCutoffDate: nextOnOrAfter(cutoffs, today),
    nextPayday: nextOnOrAfter(paydays, today),
  };
};

/**
 * Largest valid GPE (Get Paid Early) cap, expressed in days before payday, that a pay calendar can
 * actually honour.
 *
 * A GPE request can complete on any day after the cutoff (the cutoff day itself is excluded to avoid
 * timing issues) up to and including payday, so the earliest allowed day is cutoff + 1, i.e.
 * (payday - cutoff) - 1 days before payday.
 *
 * The cap is derived from the next cycle whose cutoff has not yet passed. Completed/historical cycles
 * can have wider windows (from weekend/holiday failovers shifting the cutoff earlier) that no longer
 * reflect what upcoming cycles can honour, so they are excluded.
 */
export const getGpeMaxDaysBeforePayday = (
  payCycles: PayCycle[] | undefined,
  today: Date = new Date()
): number => {
  const nextCycle = (payCycles ?? [])
    .filter((cycle) => cycle.cutoffDate && cycle.payday)
    .filter((cycle) => differenceInCalendarDays(parseISO(cycle.cutoffDate), today) >= 0)
    .sort((a, b) => differenceInCalendarDays(parseISO(a.cutoffDate), parseISO(b.cutoffDate)))[0];

  if (!nextCycle) return 0;

  const gap = differenceInCalendarDays(parseISO(nextCycle.payday), parseISO(nextCycle.cutoffDate));

  return Math.max(gap - 1, 0);
};

/** Statuses a person can be assigned to; matches what the backend accepts on create and upload. */
export const ASSIGNABLE_PAY_CALENDAR_STATUSES: PayCalendarStatus[] = ['CREATED', 'ACTIVE'];

/** Whether the pay calendar has been archived and so can no longer be changed. */
export const isPayCalendarArchived = (payCalendar?: Pick<PayCalendar, 'status'>) =>
  payCalendar?.status === 'ARCHIVED';

/**
 * The backend's message from the first failed pay calendar request that carries one, so the user
 * learns why (for example that a calendar with ledger history must be archived), else the fallback.
 */
export const getPayCalendarErrorDescription = (errors: unknown[], fallback: string) =>
  errors
    .map(
      (error) => axios.isAxiosError<{ message?: string }>(error) && error.response?.data?.message
    )
    .find((message): message is string => !!message) || fallback;

/**
 * The value the "1st of the month accrual" dropdown uses for "Whole period - all working days". It only
 * exists in the UI: the API stores whole period as startingBalanceDays 0 with fullPeriodStartingBalance true.
 */
export const WHOLE_PERIOD_STARTING_BALANCE_DAYS = 100;

/** The "1st of the month accrual" dropdown value for a pay calendar: whole period when set, else its days. */
export const getStartingBalanceDaysOption = (
  payCalendar?: Pick<PayCalendar, 'startingBalanceDays' | 'fullPeriodStartingBalance'>
) =>
  payCalendar?.fullPeriodStartingBalance
    ? WHOLE_PERIOD_STARTING_BALANCE_DAYS
    : payCalendar?.startingBalanceDays;

/** Thrown when every request in a pay calendar batch failed, carrying each failure for the message. */
export class PayCalendarBatchError extends Error {
  constructor(public readonly reasons: unknown[]) {
    super('Every pay calendar request failed');
  }
}
